(() => {
  const STORAGE_KEY = 'prazeremti:wishlist:v1';
  let memoryItems = [];
  let storageWritable = true;
  const validItem = (item) => item && /^\d+$/.test(String(item.product_id)) && /^\d+$/.test(String(item.variant_id || 0)) && /^[\p{L}\p{N}][\p{L}\p{N}._-]*$/u.test(String(item.product_handle || ''));
  const localRepository = {
    list() {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved?.version === 1 && Array.isArray(saved.items)) memoryItems = saved.items.filter(validItem).slice(0, 200);
      } catch (_error) {}
      return memoryItems.map((item) => ({ ...item }));
    },
    write(items) {
      memoryItems = items.filter(validItem).slice(0, 200).map((item) => ({ product_id: String(item.product_id), product_handle: String(item.product_handle), variant_id: String(item.variant_id || 0), added_at: String(item.added_at || new Date().toISOString()) }));
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items: memoryItems })); storageWritable = true; }
      catch (_error) { storageWritable = false; }
      document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { source: 'local', items: memoryItems.map((item) => ({ ...item })) } }));
      return memoryItems;
    },
  };
  window.PrazerWishlist = Object.freeze({ schemaVersion: 1, persistence: 'local', getItems: () => localRepository.list() });
  const OPEN_CART_AFTER_RELOAD_KEY = 'wishlist-open-cart-after-reload';
  const state = {
    items: new Map(),
    loaded: false,
    loading: false,
    loginRequired: false,
    drawerOpen: false,
    productCache: new Map(),
    toastTimer: null,
    customCardObserver: null,
    removedKeys: new Set(),
  };

  class WishlistAuthError extends Error {}

  const root = () => document.querySelector('[data-wishlist-root]');
  const keyFor = (productId, variantId) => `${String(productId)}:${String(variantId || 0)}`;
  const digits = (value) => {
    const match = String(value || '').match(/\d+/);
    return match ? match[0] : '';
  };

  const money = (cents) => {
    const rootEl = root();
    const currency = rootEl?.dataset.currency || window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || undefined;
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
  };

  const getWishlistLocale = () => {
    const lang = (document.documentElement.lang || '').toLowerCase();
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('en')) return 'en';
    if (lang.startsWith('de')) return 'de';
    if (lang.startsWith('fr')) return 'fr';
    return 'pt';
  };

  const WISHLIST_STRINGS = {
    pt: {
      storageUnavailable: 'O navegador não permitiu guardar os favoritos. Ficam disponíveis nesta página.',
      addMessages: ['Boa escolha.', 'Ótima escolha.', 'Excelente escolha.'],
      removed: 'Removido dos favoritos.',
      addedToCart: 'Adicionado ao carrinho.',
      removeAria: 'Remover dos favoritos',
      saveAria: 'Guardar nos favoritos',
      inFavorites: 'Nos favoritos',
      addToFavorites: 'Adicionar aos favoritos',
      openFavorites: 'Abrir favoritos',
      favoriteLabel: 'Favorito',
      loginRequired: 'Inicia sessão para guardares favoritos.',
      drawerAddToCart: 'Adicionar',
      drawerAdding: 'A adicionar',
      drawerAdded: 'Adicionado',
      drawerViewProduct: 'Ver produto',
      drawerUnavailable: 'Indisponível',
      drawerLoading: 'A carregar',
      drawerLoadingEllipsis: 'A carregar...',
      drawerProductUnavailable: 'Produto indisponível',
      drawerAddToCartAria: 'Adicionar ao carrinho',
      drawerRemoveAriaTitle: 'Remover dos favoritos',
      promotion: 'Promoção',
      genericError: 'Não foi possível atualizar os favoritos.',
      unavailableProduct: 'Produto indisponível.',
      missingVariant: 'Não foi possível identificar a variante do produto.',
      addToCartError: 'Não foi possível adicionar ao carrinho.',
      missingProduct: 'Não foi possível identificar o produto.',
    },
    es: {
      storageUnavailable: 'El navegador no permitió guardar los favoritos. Siguen disponibles en esta página.',
      addMessages: ['Buena elección.', 'Muy buena elección.', 'Excelente elección.'],
      removed: 'Eliminado de favoritos.',
      addedToCart: 'Añadido al carrito.',
      removeAria: 'Quitar de favoritos',
      saveAria: 'Guardar en favoritos',
      inFavorites: 'En tus favoritos',
      addToFavorites: 'Añadir a favoritos',
      openFavorites: 'Abrir favoritos',
      favoriteLabel: 'Favorito',
      loginRequired: 'Inicia sesión para guardar tus favoritos.',
      drawerAddToCart: 'Añadir',
      drawerAdding: 'Añadiendo',
      drawerAdded: 'Añadido',
      drawerViewProduct: 'Ver producto',
      drawerUnavailable: 'No disponible',
      drawerLoading: 'Cargando',
      drawerLoadingEllipsis: 'Cargando...',
      drawerProductUnavailable: 'Producto no disponible',
      drawerAddToCartAria: 'Añadir al carrito',
      drawerRemoveAriaTitle: 'Quitar de favoritos',
      promotion: 'Promoción',
      genericError: 'No se pudieron actualizar los favoritos.',
      unavailableProduct: 'Producto no disponible.',
      missingVariant: 'No se pudo identificar la variante del producto.',
      addToCartError: 'No se pudo añadir al carrito.',
      missingProduct: 'No se pudo identificar el producto.',
    },
    en: {
      storageUnavailable: 'Your browser could not save favorites. They remain available on this page.',
      addMessages: ['Good choice.', 'Great choice.', 'Excellent choice.'],
      removed: 'Removed from favorites.',
      addedToCart: 'Added to cart.',
      removeAria: 'Remove from favorites',
      saveAria: 'Save to favorites',
      inFavorites: 'In your favorites',
      addToFavorites: 'Add to favorites',
      openFavorites: 'Open favorites',
      favoriteLabel: 'Favorite',
      loginRequired: 'Sign in to save favorites.',
      drawerAddToCart: 'Add',
      drawerAdding: 'Adding',
      drawerAdded: 'Added',
      drawerViewProduct: 'View product',
      drawerUnavailable: 'Unavailable',
      drawerLoading: 'Loading',
      drawerLoadingEllipsis: 'Loading...',
      drawerProductUnavailable: 'Product unavailable',
      drawerAddToCartAria: 'Add to cart',
      drawerRemoveAriaTitle: 'Remove from favorites',
      promotion: 'Sale',
      genericError: 'Favorites could not be updated.',
      unavailableProduct: 'Product unavailable.',
      missingVariant: 'The product variant could not be identified.',
      addToCartError: 'The product could not be added to the cart.',
      missingProduct: 'The product could not be identified.',
    },
    de: {
      storageUnavailable: 'Dein Browser konnte die Favoriten nicht speichern. Sie bleiben auf dieser Seite verfügbar.',
      addMessages: ['Gute Wahl.', 'Sehr gute Wahl.', 'Ausgezeichnete Wahl.'],
      removed: 'Aus den Favoriten entfernt.',
      addedToCart: 'Zum Warenkorb hinzugefügt.',
      removeAria: 'Aus Favoriten entfernen',
      saveAria: 'In Favoriten speichern',
      inFavorites: 'In deinen Favoriten',
      addToFavorites: 'Zu Favoriten hinzufügen',
      openFavorites: 'Favoriten öffnen',
      favoriteLabel: 'Favorit',
      loginRequired: 'Melde dich an, um Favoriten zu speichern.',
      drawerAddToCart: 'Hinzufügen',
      drawerAdding: 'Wird hinzugefügt',
      drawerAdded: 'Hinzugefügt',
      drawerViewProduct: 'Produkt ansehen',
      drawerUnavailable: 'Nicht verfügbar',
      drawerLoading: 'Wird geladen',
      drawerLoadingEllipsis: 'Wird geladen…',
      drawerProductUnavailable: 'Produkt nicht verfügbar',
      drawerAddToCartAria: 'In den Warenkorb',
      drawerRemoveAriaTitle: 'Aus Favoriten entfernen',
      promotion: 'Angebot',
      genericError: 'Die Favoriten konnten nicht aktualisiert werden.',
      unavailableProduct: 'Produkt nicht verfügbar.',
      missingVariant: 'Die Produktvariante konnte nicht ermittelt werden.',
      addToCartError: 'Das Produkt konnte nicht zum Warenkorb hinzugefügt werden.',
      missingProduct: 'Das Produkt konnte nicht ermittelt werden.',
    },
  };

  WISHLIST_STRINGS.fr = {
    addMessages: ['Bon choix.', 'Très bon choix.'], removed: 'Retiré des favoris.', addedToCart: 'Ajouté au panier.', removeAria: 'Retirer des favoris', saveAria: 'Enregistrer dans les favoris', inFavorites: 'Dans vos favoris', addToFavorites: 'Ajouter aux favoris', openFavorites: 'Ouvrir les favoris', favoriteLabel: 'Favori', loginRequired: '', drawerAddToCart: 'Ajouter', drawerAdding: 'Ajout en cours', drawerAdded: 'Ajouté', drawerViewProduct: 'Voir le produit', drawerUnavailable: 'Indisponible', drawerLoading: 'Chargement', drawerLoadingEllipsis: 'Chargement…', drawerProductUnavailable: 'Produit indisponible', drawerAddToCartAria: 'Ajouter au panier', drawerRemoveAriaTitle: 'Retirer des favoris', promotion: 'Promotion', genericError: 'Impossible de mettre à jour les favoris.', unavailableProduct: 'Produit indisponible.', missingVariant: 'Impossible d’identifier la variante.', addToCartError: 'Impossible d’ajouter le produit au panier.', missingProduct: 'Impossible d’identifier le produit.', storageUnavailable: 'Le navigateur n’a pas pu enregistrer les favoris. Ils restent disponibles sur cette page.'
  };
  const wt = (key) => WISHLIST_STRINGS[getWishlistLocale()][key];

  const heartSvg = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.67 5.47c-1.99-1.64-4.95-1.35-6.78.48L12 6.84l-.89-.89c-1.82-1.82-4.79-2.12-6.78-.48-2.28 1.88-2.4 5.27-.36 7.31l7.67 7.67c.2.2.51.2.71 0l7.67-7.67c2.05-2.04 1.93-5.42-.35-7.31Z"></path>
    </svg>
  `;

  const removeIconSvg = () => `
    <svg class="wishlist-drawer__remove-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 7.2V5.8c0-.85.68-1.55 1.53-1.55h2.94c.85 0 1.53.7 1.53 1.55v1.4"></path>
      <path d="M5.9 7.2h12.2"></path>
      <path d="M7.35 7.2l.68 11.05c.06.94.84 1.68 1.78 1.68h4.38c.94 0 1.72-.74 1.78-1.68l.68-11.05"></path>
      <path d="M10.6 10.4v6.05"></path>
      <path d="M13.4 10.4v6.05"></path>
    </svg>
  `;

  const bagIconSvg = () => `
    <svg class="wishlist-drawer__bag-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.45 8.35h11.1l.82 11.05H5.63L6.45 8.35z"></path>
      <path d="M9.1 8.35V6.65a2.9 2.9 0 0 1 5.8 0v1.7"></path>
    </svg>
  `;

  const addCartButtonHtml = (label) => `<span class="wishlist-drawer__add-label">${label || wt('drawerAddToCart')}</span>${bagIconSvg()}`;

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const toastHeartSvg = () => `
    <svg class="wishlist-toast__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.55 5.55c-1.9-1.7-4.78-1.48-6.62.36L12 6.84l-.93-.93C9.23 4.07 6.35 3.85 4.45 5.55c-2.2 1.96-2.22 5.28-.17 7.33l7.3 7.28c.23.23.61.23.84 0l7.3-7.28c2.05-2.05 2.03-5.37-.17-7.33Z"></path>
    </svg>
  `;

  const toastBrokenHeartSvg = () => `
    <svg class="wishlist-toast__icon wishlist-toast__icon--broken" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.55 5.55c-1.9-1.7-4.78-1.48-6.62.36L12 6.84l-.93-.93C9.23 4.07 6.35 3.85 4.45 5.55c-2.2 1.96-2.22 5.28-.17 7.33l7.3 7.28c.23.23.61.23.84 0l7.3-7.28c2.05-2.05 2.03-5.37-.17-7.33Z"></path>
      <path d="M12.65 7.05 10.6 10.1l2.65 1.5-2.2 4.15"></path>
    </svg>
  `;

  const toastBagSvg = () => `
    <svg class="wishlist-toast__icon wishlist-toast__icon--bag" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.45 8.35h11.1l.82 11.05H5.63L6.45 8.35z"></path>
      <path d="M9.1 8.35V6.65a2.9 2.9 0 0 1 5.8 0v1.7"></path>
    </svg>
  `;

  const randomWishlistAddMessage = () => {
    const messages = wt('addMessages');
    return messages[Math.floor(Math.random() * messages.length)];
  };


  const requestJson = async (route, options = {}) => {
    let items = localRepository.list();
    const item = options.body;
    if (route === 'add' || route === 'remove') {
      if (!validItem(item)) throw new Error(wt('missingProduct'));
      items = items.filter((saved) => keyFor(saved.product_id, saved.variant_id) !== keyFor(item.product_id, item.variant_id));
      if (route === 'add') items.unshift({ ...item, added_at: new Date().toISOString() });
      items = localRepository.write(items);
    } else if (route !== 'list') throw new Error(wt('genericError'));
    return { ok: true, items, item: items.find((saved) => item && keyFor(saved.product_id, saved.variant_id) === keyFor(item.product_id, item.variant_id)), count: items.length, persisted: storageWritable };
  };

  const showToast = (message, explicitType = '') => {
    const toast = document.querySelector('[data-wishlist-toast]');
    if (!toast) return;

    const normalizedMessage = String(message || '');
    const lowerMessage = normalizedMessage.toLowerCase();
    const toastType = explicitType || (
      lowerMessage.includes('remov')
        ? 'removed'
        : lowerMessage.includes('favorit') || lowerMessage.includes('escolha')
          ? 'added'
          : lowerMessage.includes('carrinho') || lowerMessage.includes('adicionado')
            ? 'cart'
            : 'notice'
    );

    const icon = toastType === 'removed'
      ? toastBrokenHeartSvg()
      : toastType === 'cart'
        ? toastBagSvg()
        : toastType === 'added'
          ? toastHeartSvg()
          : '';

    window.clearTimeout(state.toastTimer);
    toast.dataset.toastType = toastType;
    toast.innerHTML = `${icon}<span class="wishlist-toast__text">${escapeHtml(normalizedMessage)}</span>`;
    toast.hidden = false;
    toast.classList.remove('is-visible');

    window.requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    state.toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!toast.classList.contains('is-visible')) {
          toast.hidden = true;
        }
      }, 180);
    }, 3200);
  };

  const showDrawerMessage = (message) => {
    const messageEl = document.querySelector('[data-wishlist-message]');
    if (!messageEl) return;
    messageEl.textContent = message;
    messageEl.hidden = !message;
  };

  const setDrawerMode = (mode) => {
    const login = document.querySelector('[data-wishlist-login]');
    const empty = document.querySelector('[data-wishlist-empty]');
    const loading = document.querySelector('[data-wishlist-loading]');
    const list = document.querySelector('[data-wishlist-list]');

    if (login) login.hidden = mode !== 'login';
    if (empty) empty.hidden = mode !== 'empty';
    if (loading) loading.hidden = mode !== 'loading';

    if (list) {
      const showList = mode === 'list';
      list.hidden = !showList;
      list.setAttribute('aria-hidden', showList ? 'false' : 'true');
      list.style.display = showList ? '' : 'none';
    }
  };

  const updateCount = (count = state.items.size) => {
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      const next = Number(count || 0);
      el.textContent = String(next);
      el.hidden = next === 0;
    });
    renderWishlistPage();
  };

  const replaceWishlistItems = (items = []) => {
    const nextItems = new Map();
    (items || []).forEach((item) => {
      nextItems.set(keyFor(item.product_id, item.variant_id), item);
    });
    state.items = nextItems;
    return state.items;
  };

  const syncWishlistList = async ({ render = state.drawerOpen } = {}) => {
    const data = await requestJson('list');
    const serverItems = (data.items || []).filter((entry) => !state.removedKeys.has(keyFor(entry.product_id, entry.variant_id)));
    replaceWishlistItems(serverItems);
    state.loaded = true;
    state.loginRequired = false;
    updateCount(state.items.size);
    updateButtons();
    if (render && state.drawerOpen) renderDrawer();
    return { ...data, items: serverItems, count: state.items.size };
  };

  const updateButton = (button) => {
    const active = state.items.has(keyFor(button.dataset.productId, button.dataset.variantId));
    const text = button.querySelector('.wishlist-button__text');
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', active ? wt('removeAria') : wt('saveAria'));
    if (text && button.classList.contains('wishlist-button--product')) {
      text.textContent = active ? wt('inFavorites') : wt('addToFavorites');
    }
  };

  const updateButtons = () => {
    document.querySelectorAll('[data-wishlist-button]').forEach(updateButton);
  };

  const syncProductButtons = () => {
    document.querySelectorAll('.wishlist-button--product[data-wishlist-button]').forEach((button) => {
      const scope = button.closest('product-info') || button.closest('main') || document;
      const variantInput =
        scope.querySelector('form[action*="/cart/add"] input[name="id"]') ||
        document.querySelector('form[action*="/cart/add"] input[name="id"]');
      const variantId = digits(variantInput?.value);
      if (variantId) button.dataset.variantId = variantId;
    });
  };

  const localePathPrefix = () => (window.Shopify?.routes?.root || '/').replace(/\/$/, '');

  const productJson = (handle, fresh = false) => {
    if (fresh) state.productCache.delete(handle);
    if (state.productCache.has(handle)) return state.productCache.get(handle);
    const request = fetch(`${localePathPrefix()}/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(wt('unavailableProduct'));
        return response.json();
      })
      .catch((error) => {
        state.productCache.delete(handle);
        throw error;
      });

    state.productCache.set(handle, request);
    return request;
  };

  const resolveFavoriteVariant = (product, item) => {
    const variants = product?.variants || [];
    if (!variants.length) return null;

    const requestedVariantId = digits(item?.variant_id);
    const savedVariant = requestedVariantId
      ? variants.find((entry) => String(entry.id) === String(requestedVariantId))
      : null;

    return savedVariant || variants.find((entry) => entry.available !== false) || variants[0] || null;
  };

  const dispatchCartUpdate = async () => {
    let cart = null;
    try {
      const response = await fetch(`${localePathPrefix()}/cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (response.ok) cart = await response.json();
    } catch (error) {
      cart = null;
    }

    const detail = cart ? { cart } : {};
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart:change', { detail }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    window.dispatchEvent(new CustomEvent('cart:change', { detail }));
    window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
  };

  const addFavoriteToCart = async (item, button) => {
    if (!item?.product_handle || !button) return;

    const previousHtml = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<span class="wishlist-drawer__add-label">${wt('drawerAdding')}</span>`;

    try {
      const product = await productJson(item.product_handle, true);
      const variant = resolveFavoriteVariant(product, {
        ...item,
        variant_id: button.dataset.variantId || item.variant_id,
      });

      if (!variant?.id) {
        throw new Error(wt('missingVariant'));
      }

      if (variant.available === false) {
        throw new Error(wt('unavailableProduct'));
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const response = await fetch(`${localePathPrefix()}/cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: Number(variant.id),
          quantity: 1,
          sections: cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [],
          sections_url: window.location.pathname,
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.description || data?.message || wt('addToCartError'));
      }

      showToast(wt('addedToCart'), 'cart');
      closeDrawer();
      if (cartDrawer?.renderContents && data?.sections) {
        await cartDrawer.animateAddition?.(button);
        cartDrawer.setActiveElement?.(button);
        cartDrawer.renderContents(data);
        await dispatchCartUpdate();
        button.disabled = false;
        button.innerHTML = previousHtml || addCartButtonHtml();
      } else {
        window.location.assign(`${localePathPrefix()}/cart`);
      }

    } catch (error) {
      button.disabled = false;
      button.innerHTML = previousHtml || addCartButtonHtml();
      showToast(error.message || wt('addToCartError'));
    }
  };

  const createHeaderButton = () => {
    const existingButton = document.querySelector('[data-wishlist-header-trigger]');
    if (existingButton) {
      if (existingButton.dataset.wishlistBound !== 'true') {
        existingButton.dataset.wishlistBound = 'true';
        existingButton.setAttribute('aria-label', wt('openFavorites'));
        existingButton.addEventListener('click', openDrawer);
      }
      return;
    }
    const parent =
      document.querySelector('[class*="ai-header-right-"]') ||
      document.querySelector('.header__icons') ||
      document.querySelector('header');
    if (!parent) return;

    const button = document.createElement('button');
    const idMatch = String(parent.className || '').match(/ai-header-right-([a-z0-9]+)/i);
    button.type = 'button';
    button.className = idMatch ? `wishlist-header-trigger ai-header-icon-${idMatch[1]}` : 'wishlist-header-trigger header__icon link focus-inset';
    button.dataset.wishlistHeaderTrigger = '';
    button.dataset.wishlistBound = 'true';
    button.setAttribute('aria-label', wt('openFavorites'));
    button.innerHTML = `${heartSvg()}<span class="wishlist-count-bubble" data-wishlist-count hidden>0</span>`;
    button.addEventListener('click', openDrawer);

    const cart = parent.querySelector('[href*="/cart"], #cart-icon-bubble');
    parent.insertBefore(button, cart || null);
  };

  const customCardTargets = [
    {
      card: 'a[class*="ai-featured-collection-product-"]',
      link: null,
    },
    {
      card: '[class*="ai-luxury-carousel-slide-"]',
      link: 'a[class*="ai-luxury-carousel-product-link-"]',
    },
    {
      card: '[class*="ai-luxury-product-grid__item-"]',
      link: 'a[class*="ai-luxury-product-grid__image-wrapper-"]',
    },
    {
      card: 'a[class*="ai-premium-grid__card-link-"]',
      link: null,
    },
    {
      card: 'a[class*="ai-bestsellers-carousel__card-"]',
      link: null,
    },
    {
      card: '[class*="ai-product-grid__item-"]',
      link: 'a[class*="ai-product-grid__image-link-"]',
    },
    {
      card: '[class*="ai-product-card-"]',
      link: 'a[class*="ai-product-image-wrapper-"]',
    },
  ];

  const customCardSelector = customCardTargets.map((target) => target.card).join(',');
  const customCardTargetFor = (card) => customCardTargets.find((target) => card.matches(target.card));

  const productHandleFromUrl = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const customButtonHtml = () => `${heartSvg()}<span class="wishlist-button__text">${wt('favoriteLabel')}</span>`;

  const createCustomCardButton = (product, handle) => {
    const variant = product.variants?.[0] || {};
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wishlist-button wishlist-button--card';
    button.dataset.wishlistButton = '';
    button.dataset.productId = digits(product.id);
    button.dataset.productHandle = product.handle || handle;
    button.dataset.variantId = digits(variant.id) || '0';
    button.setAttribute('aria-label', wt('saveAria'));
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = customButtonHtml();
    return button;
  };

  const hydrateCustomCard = async (card, link) => {
    if (!card || card.dataset.wishlistScopedHydrated || card.querySelector('[data-wishlist-button]')) return;

    const href = link?.href || card.href;
    const handle = productHandleFromUrl(href);
    if (!handle) return;

    card.dataset.wishlistScopedHydrated = 'loading';
    try {
      const product = await productJson(handle);
      const button = createCustomCardButton(product, handle);
      const currentPosition = window.getComputedStyle(card).position;
      if (currentPosition === 'static') {
        card.style.position = 'relative';
      }
      card.appendChild(button);
      updateButton(button);
      card.dataset.wishlistScopedHydrated = 'true';
    } catch (error) {
      delete card.dataset.wishlistScopedHydrated;
    }
  };

  const queueCustomCardHydration = (card) => {
    if (!card || card.dataset.wishlistScopedHydrated || card.dataset.wishlistHydrationQueued) return;
    if (card.querySelector('[data-wishlist-button]')) return;

    const target = customCardTargetFor(card);
    if (!target) return;

    const hydrate = () => {
      delete card.dataset.wishlistHydrationQueued;
      if (!document.documentElement.contains(card)) return;
      const link = target.link ? card.querySelector(target.link) : card;
      hydrateCustomCard(card, link);
    };

    if (!('IntersectionObserver' in window)) {
      hydrate();
      return;
    }

    if (!state.customCardObserver) {
      state.customCardObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          delete entry.target.dataset.wishlistHydrationQueued;

          if (!document.documentElement.contains(entry.target)) return;
          const observedTarget = customCardTargetFor(entry.target);
          if (!observedTarget) return;
          const link = observedTarget.link ? entry.target.querySelector(observedTarget.link) : entry.target;
          hydrateCustomCard(entry.target, link);
        });
      }, { rootMargin: '400px 0px' });
    }

    card.dataset.wishlistHydrationQueued = 'true';
    state.customCardObserver.observe(card);
  };

  const hydrateCustomCards = (scope = document) => {
    if (scope.matches && scope.matches(customCardSelector)) queueCustomCardHydration(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(customCardSelector).forEach(queueCustomCardHydration);
  };

  const hydrateInterface = () => {
    createHeaderButton();
    hydrateCustomCards();
    syncProductButtons();
    updateButtons();
    updateCount();
  };

  const loadWishlist = async () => {
    if (state.loading) return;
    state.loading = true;
    try {
      await syncWishlistList({ render: state.drawerOpen });
    } catch (error) {
      if (error instanceof WishlistAuthError) {
        state.loaded = true;
        if (state.drawerOpen) renderDrawer();
      } else {
        showToast(error.message);
      }
    } finally {
      state.loading = false;
    }
  };

  const onWishlistButtonClick = async (event) => {
    const button = event.target.closest('[data-wishlist-button]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    syncProductButtons();

    const payload = {
      product_id: button.dataset.productId,
      product_handle: button.dataset.productHandle,
      variant_id: button.dataset.variantId || '0',
    };

    if (!payload.product_id || !payload.product_handle) {
      showToast(wt('missingProduct'));
      return;
    }

    const key = keyFor(payload.product_id, payload.variant_id);
    const isActive = state.items.has(key);
    const route = isActive ? 'remove' : 'add';
    const previousItems = new Map(state.items);
    const previousRemovedKeys = new Set(state.removedKeys);

    button.disabled = true;
    if (isActive) {
      state.removedKeys.add(key);
      state.items.delete(key);
    } else {
      state.removedKeys.delete(key);
      state.items.set(key, {
        product_id: payload.product_id,
        product_handle: payload.product_handle,
        variant_id: payload.variant_id || '0',
        added_at: new Date().toISOString(),
      });
    }
    updateCount(state.items.size);
    updateButtons();
    if (state.drawerOpen) renderDrawer();

    try {
      const data = await requestJson(route, { method: 'POST', body: payload });
      if (isActive) {
        showToast(data.persisted ? wt('removed') : wt('storageUnavailable'), 'removed');
      } else {
        state.items.set(key, data.item || {
          product_id: payload.product_id,
          product_handle: payload.product_handle,
          variant_id: payload.variant_id || '0',
          added_at: new Date().toISOString(),
        });
        showToast(data.persisted ? randomWishlistAddMessage() : wt('storageUnavailable'), 'added');
      }
      state.loginRequired = false;
      updateCount(data.count ?? state.items.size);
      updateButtons();
      if (state.drawerOpen) renderDrawer();
    } catch (error) {
      state.items = previousItems;
      state.removedKeys = previousRemovedKeys;
      updateCount(state.items.size);
      updateButtons();
      if (state.drawerOpen) renderDrawer();
      if (error instanceof WishlistAuthError) {
        showToast(wt('loginRequired'));
        openDrawer();
      } else {
        showToast(error.message);
      }
    } finally {
      button.disabled = false;
    }
  };

  const openDrawer = () => {
    const rootEl = root();
    const drawer = document.querySelector('#WishlistDrawer');
    if (!rootEl || !drawer) return;
    drawer.hidden = false;
    state.drawerOpen = true;
    rootEl.classList.add('is-open');
    document.body.classList.add('wishlist-drawer-open');
    drawer.focus({ preventScroll: true });
    if (!state.loaded && !state.loading) {
      setDrawerMode('loading');
      loadWishlist();
    } else {
      renderDrawer();
    }
  };

  const closeDrawer = () => {
    const rootEl = root();
    const drawer = document.querySelector('#WishlistDrawer');
    if (!rootEl || !drawer) return;
    state.drawerOpen = false;
    rootEl.classList.remove('is-open');
    document.body.classList.remove('wishlist-drawer-open');
    window.setTimeout(() => {
      if (!state.drawerOpen) drawer.hidden = true;
    }, 240);
  };

  const imageUrl = (product, variant) => {
    const source = variant?.featured_image?.src || product.featured_image || product.images?.[0] || '';
    if (!source) return '';
    if (source.startsWith('//')) return `https:${source}`;
    return source;
  };

  const priceInfo = (product, variant) => {
    const current = Number(variant?.price ?? product?.price ?? 0);
    const compare = Number(variant?.compare_at_price ?? product?.compare_at_price ?? 0);
    const onSale = compare > current && current > 0;
    return { current, compare, onSale };
  };

  const drawerPriceHtml = (product, variant) => {
    const info = priceInfo(product, variant);
    if (!info.current) return '';

    if (!info.onSale) {
      return `<span class="wishlist-drawer__price-current">${money(info.current)}</span>`;
    }

    return `
      <span class="wishlist-drawer__price-current wishlist-drawer__price-current--sale">${money(info.current)}</span>
      <span class="wishlist-drawer__price-compare">${money(info.compare)}</span>
      <span class="wishlist-drawer__sale-badge">${wt('promotion')}</span>
    `;
  };

  const renderDrawerItem = (item) => {
    const li = document.createElement('li');
    li.className = 'wishlist-drawer__item';
    li.dataset.key = keyFor(item.product_id, item.variant_id);
    li.innerHTML = `
      <div class="wishlist-drawer__image-frame"><div class="wishlist-drawer__image-placeholder"></div></div>
      <div class="wishlist-drawer__item-body">
        <h3 class="wishlist-drawer__item-title">${wt('drawerLoadingEllipsis')}</h3>
        <p class="wishlist-drawer__price"></p>
        <div class="wishlist-drawer__actions">
          <button type="button" class="wishlist-drawer__add-to-cart" disabled aria-label="${wt('drawerAddToCartAria')}"><span class="wishlist-drawer__add-label">${wt('drawerLoading')}</span></button>
          <a class="wishlist-drawer__product-link" href="/products/${encodeURIComponent(item.product_handle)}">${wt('drawerViewProduct')}</a>
          <button type="button" class="wishlist-drawer__remove" aria-label="${wt('drawerRemoveAriaTitle')}" title="${wt('drawerRemoveAriaTitle')}">${removeIconSvg()}</button>
        </div>
      </div>
    `;

    const addToCartButton = li.querySelector('.wishlist-drawer__add-to-cart');
    li.querySelector('.wishlist-drawer__remove').addEventListener('click', () => removeFavorite(item));
    addToCartButton.addEventListener('click', () => addFavoriteToCart(item, addToCartButton));

    productJson(item.product_handle)
      .then((product) => {
        li.querySelector('.wishlist-drawer__item-title').textContent = product.title || item.product_handle;
        const selected = resolveFavoriteVariant(product, item);
        const refreshVariant = (variant) => {
          const image = imageUrl(product, variant);
          const frame = li.querySelector('.wishlist-drawer__image-frame');
          if (image && frame) {
            const img = document.createElement('img');
            img.className = 'wishlist-drawer__image'; img.src = image;
            img.alt = product.title || item.product_handle; img.loading = 'lazy';
            frame.replaceChildren(img);
          }
          const info = priceInfo(product, variant);
          li.classList.toggle('wishlist-drawer__item--sale', info.onSale);
          li.querySelector('.wishlist-drawer__price').innerHTML = drawerPriceHtml(product, variant);
          const link = li.querySelector('.wishlist-drawer__product-link');
          link.href = localePathPrefix() + '/products/' + encodeURIComponent(item.product_handle) + (variant?.id ? '?variant=' + variant.id : '');
          addToCartButton.disabled = !variant?.available;
          addToCartButton.dataset.variantId = String(variant?.id || '');
          addToCartButton.innerHTML = variant?.available ? addCartButtonHtml() : '<span class="wishlist-drawer__add-label">' + wt('drawerUnavailable') + '</span>';
        };
        if (product.variants?.length > 1) {
          const select = document.createElement('select');
          select.className = 'wishlist-drawer__variants';
          select.setAttribute('aria-label', product.title);
          product.variants.forEach((variant) => {
            const option = new Option(variant.title + (variant.available ? '' : ' — ' + wt('drawerUnavailable')), String(variant.id));
            option.disabled = !variant.available;
            option.selected = String(variant.id) === String(selected?.id);
            select.append(option);
          });
          select.addEventListener('change', () => refreshVariant(product.variants.find((v) => String(v.id) === select.value)));
          li.querySelector('.wishlist-drawer__price').after(select);
        }
        refreshVariant(selected);
      })
      .catch(() => {
        li.querySelector('.wishlist-drawer__item-title').textContent = item.product_handle;
        li.querySelector('.wishlist-drawer__price').textContent = wt('drawerProductUnavailable');
        addToCartButton.disabled = true;
        addToCartButton.innerHTML = `<span class="wishlist-drawer__add-label">${wt('drawerUnavailable')}</span>`;
      });

    return li;
  };

  const clearDrawerList = () => {
    const list = document.querySelector('[data-wishlist-list]');
    if (list) list.innerHTML = '';
  };

  function renderWishlistPage() {
    const list = document.querySelector('[data-wishlist-page-list]');
    if (!list) return;
    const items = [...state.items.values()].filter((item) => !state.removedKeys.has(keyFor(item.product_id, item.variant_id)));
    list.replaceChildren(...items.map(renderDrawerItem));
    const empty = document.querySelector('[data-wishlist-page-empty]');
    if (empty) empty.hidden = items.length !== 0;
  }

  const renderDrawer = () => {
    showDrawerMessage('');

    if (state.loginRequired) {
      clearDrawerList();
      setDrawerMode('login');
      return;
    }

    const items = Array.from(state.items.values())
      .filter((item) => !state.removedKeys.has(keyFor(item.product_id, item.variant_id)))
      .sort((a, b) => String(b.added_at || '').localeCompare(String(a.added_at || '')));

    const list = document.querySelector('[data-wishlist-list]');
    if (!list) return;

    list.innerHTML = '';

    if (!items.length) {
      setDrawerMode('empty');
      return;
    }

    items.forEach((item) => list.appendChild(renderDrawerItem(item)));
    setDrawerMode('list');
  };

  const removeFavorite = async (item) => {
    const key = keyFor(item.product_id, item.variant_id);
    const previousItems = new Map(state.items);
    const itemEl = document.querySelector(`[data-key="${CSS.escape(key)}"]`);
    const removeButton = itemEl?.querySelector('.wishlist-drawer__remove');

    if (removeButton) removeButton.disabled = true;
    state.removedKeys.add(key);
    state.items.delete(key);
    if (itemEl) itemEl.remove();
    updateCount(state.items.size);
    updateButtons();
    if (state.drawerOpen) renderDrawer();

    try {
      const data = await requestJson('remove', {
        method: 'POST',
        body: {
          product_id: item.product_id,
          product_handle: item.product_handle,
          variant_id: item.variant_id || 0,
        },
      });

      updateCount(data.count ?? state.items.size);
      showToast(data.persisted ? wt('removed') : wt('storageUnavailable'), 'removed');
    } catch (error) {
      state.removedKeys.delete(key);
      state.items = previousItems;
      updateCount(state.items.size);
      updateButtons();
      if (state.drawerOpen) renderDrawer();
      showToast(error.message);
    } finally {
      if (removeButton && document.body.contains(removeButton)) removeButton.disabled = false;
    }
  };

  const reopenCartAfterReload = () => {
    try {
      if (window.sessionStorage.getItem(OPEN_CART_AFTER_RELOAD_KEY) !== 'true') return;
      window.sessionStorage.removeItem(OPEN_CART_AFTER_RELOAD_KEY);
    } catch (_error) {
      return;
    }

    const openCart = () => {
      const drawer = document.querySelector('cart-drawer');
      const trigger = document.querySelector('#cart-icon-bubble, [data-ai-header-cart-link], a[href$="/cart"]');
      if (drawer?.classList.contains('active')) return;
      if (typeof drawer?.open === 'function') drawer.open(trigger || undefined);
      else trigger?.click();
    };

    customElements.whenDefined('cart-drawer')
      .then(() => window.setTimeout(openCart, 180))
      .catch(() => undefined);
  };

  const setupEvents = () => {
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      memoryItems = [];
      replaceWishlistItems(localRepository.list());
      state.removedKeys.clear();
      updateCount(); updateButtons();
      if (state.drawerOpen) renderDrawer();
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-wishlist-close]')) closeDrawer();
      onWishlistButtonClick(event);
    });
    document.addEventListener('change', () => {
      syncProductButtons();
      updateButtons();
    });
    document.addEventListener('variant:change', () => {
      syncProductButtons();
      updateButtons();
    });
    document.addEventListener('optionValueSelectionChange', () => {
      window.setTimeout(() => {
        syncProductButtons();
        updateButtons();
      }, 0);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
    });
    document.addEventListener('wishlist:hydrate', (event) => {
      hydrateCustomCards(event.detail?.root || document);
    });
    document.addEventListener('shopify:section:load', (event) => {
      hydrateCustomCards(event.target || document);
    });
  };

  const init = () => {
    if (!root()) return;
    hydrateInterface();
    setupEvents();
    loadWishlist();
    reopenCartAfterReload();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
  Some product images contain copy baked into the bitmap. Keep the Portuguese
  source media untouched and swap only the approved localized file in the
  matching storefront language. This also covers images injected after a cart,
  wishlist or section refresh.
*/
(() => {
  const locale = String(document.documentElement.lang || '').toLowerCase().split('-')[0];
  const localizedMediaByLocale = {
    es: {
      'product-media-webp-01_ca3a0fef-7812-4912-a58f-f29c5351ed77.webp':
        'https://cdn.shopify.com/s/files/1/0972/4782/2163/files/loc-es-20260808-isabella-features-v2.png?v=1786185465',
      'product-media-webp-07_28e7ff40-a6d4-44be-a30b-5ba246e546b8.webp':
        'https://cdn.shopify.com/s/files/1/0972/4782/2163/files/loc-es-20260808-sofia-features.png?v=1786183869',
      'product-media-webp-11_fd06628e-96da-4a2b-bb9d-3a273f7dbd8b.webp':
        'https://cdn.shopify.com/s/files/1/0972/4782/2163/files/loc-es-20260808-martina-features.png?v=1786183855',
    },
    de: {
      'product-media-webp-01_ca3a0fef-7812-4912-a58f-f29c5351ed77.webp':
        'https://cdn.shopify.com/s/files/1/0972/4782/2163/files/loc-de-20260808-isabella-features.webp?v=1786183161',
      'product-media-webp-07_28e7ff40-a6d4-44be-a30b-5ba246e546b8.webp':
        'https://cdn.shopify.com/s/files/1/0972/4782/2163/files/loc-de-20260811-v2-product-sofia-features.png?v=1786455595',
      'product-media-webp-11_fd06628e-96da-4a2b-bb9d-3a273f7dbd8b.webp':
        'https://cdn.shopify.com/s/files/1/0972/4782/2163/files/loc-de-20260811-v2-product-martina-features.png?v=1786455587',
    },
  };
  const localizedMedia = localizedMediaByLocale[locale];
  if (!localizedMedia) return;

  const sourceAttributes = ['src', 'srcset', 'data-src', 'data-srcset', 'data-pt-src', 'data-pt-srcset'];

  const localizeMediaNode = (node) => {
    if (!(node instanceof Element)) return;
    const candidates = node.matches('img, source') ? [node] : Array.from(node.querySelectorAll('img, source'));

    candidates.forEach((candidate) => {
      sourceAttributes.forEach((attribute) => {
        const currentValue = candidate.getAttribute(attribute);
        if (!currentValue) return;

        const originalName = Object.keys(localizedMedia).find((name) => currentValue.includes(name));
        if (!originalName) return;

        const localizedUrl = localizedMedia[originalName];
        candidate.setAttribute(attribute, attribute.includes('srcset') ? `${localizedUrl} 1000w` : localizedUrl);
      });
    });
  };

  const startLocalizedMedia = () => {
    localizeMediaNode(document.documentElement);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') localizeMediaNode(mutation.target);
        mutation.addedNodes.forEach((node) => localizeMediaNode(node));
      });
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: sourceAttributes,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startLocalizedMedia, { once: true });
  } else {
    startLocalizedMedia();
  }
})();


